<template>
    <div class="timeline-page relative flex flex-col flex-1 min-h-0 bg-stage-background overflow-hidden">
        <div class="stage-page-header">
            <div class="stage-page-header-row">
                <div class="stage-page-header-main">
                    <div class="flex flex-wrap items-center gap-2">
                        <h1 class="stage-page-title">{{ $t('Timeline') }}</h1>
                        <div class="stage-shortcut-badges">
                            <span
                                v-for="badge in $navigationShortcutBadges('timeline')"
                                :key="badge.key"
                                :title="badge.title || $t('Shortcut')"
                                class="stage-shortcut-badge"
                            >
                                <svg-vue class="stage-shortcut-badge-icon" icon="font-awesome.solid.command"></svg-vue>
                                {{ badge.label }}
                            </span>
                        </div>
                    </div>
                </div>
                <div class="stage-page-header-actions">
                    <button
                        class="btn-stage-icon-plain"
                        :aria-label="$t('Add')"
                        :title="$t('Add')"
                        type="button"
                        @click="openNewTimelineEntryDialog"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.plus"></svg-vue>
                    </button>
                    <button
                        :disabled="exporting"
                        :aria-label="$t('Export')"
                        class="btn-stage-icon-plain"
                        :title="$t('Export')"
                        type="button"
                        @click="exportTimeline"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrow-down-to-line"></svg-vue>
                    </button>
                    <button
                        :aria-label="$t('Filter menu')"
                        class="btn-stage-icon-plain"
                        :title="$t('Filter menu')"
                        type="button"
                        @click="toggleFilterSidebar"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.sliders"></svg-vue>
                    </button>
                    <button
                        :aria-label="$t('Reload')"
                        class="btn-stage-icon-plain"
                        :title="$t('Reload')"
                        type="button"
                        @click="onRefreshTimeline"
                    >
                        <svg-vue class="h-4 w-4" icon="font-awesome.regular.arrows-repeat"></svg-vue>
                    </button>
                </div>
            </div>
        </div>

        <div class="stage-page-body stage-page-fill">
            <div class="w-full h-full bg-white shadow rounded-md relative overflow-hidden flex flex-col min-h-0">
                <loading :status="loading"/>

                <div class="relative flex-1 min-h-0 overflow-hidden">
                    <template v-if="!loading">
                        <div ref="timelineShell" class="timeline-shell" @wheel="onTimelineShellWheel" @scroll.passive="onTimelineShellScroll">
                            <div class="timeline-header border-b border-ultralight" :style="{ minWidth: timelineWidth + 'px', width: timelineWidth + 'px' }">
                                <div class="timeline-right-header" :style="{ width: timelineWidth + 'px' }">
                                    <div class="timeline-month-row border-b border-ultralight">
                                        <div
                                            v-for="month in months"
                                            :key="'month-' + month.key"
                                            class="timeline-month-cell border-r border-bordergray-100"
                                            :style="{ left: month.left + 'px', width: month.width + 'px' }"
                                        >
                                            {{ month.label }}
                                        </div>
                                    </div>
                                    <div class="timeline-week-row">
                                        <div
                                            v-for="week in weeks"
                                            :key="'week-' + week.key"
                                            class="timeline-week-marker border-r border-bordergray-100"
                                            :style="{ left: week.left + 'px', width: week.width + 'px' }"
                                        >
                                            <app-tooltip
                                                class="timeline-week-tooltip"
                                                placement="bottom"
                                                width-class="w-auto max-w-none"
                                            >
                                                <span class="timeline-week-label">KW {{ week.week }}</span>
                                                <template #content>
                                                    <span class="timeline-week-tooltip-text">{{ weekRangeText(week) }}</span>
                                                </template>
                                            </app-tooltip>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            <div class="timeline-body" :style="{ minWidth: timelineWidth + 'px', width: timelineWidth + 'px' }">
                                <div class="timeline-grid-fill" :style="{ left: '0px', width: timelineWidth + 'px' }">
                                    <div
                                        v-for="week in weeks"
                                        :key="'fill-week-' + week.key"
                                        class="timeline-grid-fill-cell border-r border-bordergray-100"
                                        :style="{ left: week.left + 'px', width: week.width + 'px' }"
                                    ></div>
                                </div>
                                <div class="timeline-rows-overlay" :style="{ minWidth: timelineWidth + 'px', width: timelineWidth + 'px' }">
                                    <div
                                        v-if="todayLeft !== null"
                                        class="timeline-today-overlay"
                                        :style="{ left: todayLeft + 'px' }"
                                    ></div>
                                    <div
                                        v-for="row in rows"
                                        :key="'row-' + (row.type || 'position') + '-' + row.id"
                                        class="timeline-row border-b border-bordergray-100"
                                        :style="{ minWidth: timelineWidth + 'px', width: timelineWidth + 'px' }"
                                    >
                                        <div
                                            class="timeline-right-cell"
                                            :class="{'timeline-right-cell-menu-open': rowHasActiveMenu(row)}"
                                            :style="{ width: timelineWidth + 'px', height: rowHeight(row) + 'px' }"
                                        >
                                            <div :class="hasSegments(row) ? 'timeline-track-label-wrap' : 'timeline-track-label-wrap timeline-track-label-wrap-no-segments'">
                                                <component
                                                    :is="rowManageRoute(row) ? 'router-link' : 'div'"
                                                    :to="rowManageRoute(row) || undefined"
                                                    class="timeline-track-label"
                                                >
                                                    <img
                                                        v-if="rowAvatar(row) && getUserAvatarUrl(rowAvatar(row))"
                                                        :src="getUserAvatarUrl(rowAvatar(row))"
                                                        :alt="rowAvatar(row).name || $t('Avatar')"
                                                        :title="rowAvatar(row).name || $t('Avatar')"
                                                        class="timeline-track-avatar"
                                                    >
                                                    <div
                                                        v-if="rowProjects(row).length > 0"
                                                        class="timeline-track-projects"
                                                    >
                                                        <img
                                                            v-for="(project, projectIndex) in rowProjects(row)"
                                                            :key="'row-project-' + row.id + '-' + project.id + '-' + projectIndex"
                                                            :src="rowProjectImageUrl(project)"
                                                            :alt="project.title || $t('Project image')"
                                                            :title="rowProjectTooltip(project)"
                                                            :class="projectIndex === rowProjects(row).length - 1 ? '' : '-mr-4'"
                                                            class="timeline-track-project-avatar"
                                                        >
                                                    </div>
                                                    <span class="timeline-track-title">{{ rowDisplayTitle(row) }}</span>
                                                    <span v-if="chronologyStatusText(row)" class="timeline-track-meta-label">{{ chronologyStatusText(row) }}</span>
                                                </component>
                                            </div>
                                            <div
                                                v-for="segment in timelineSegments(row)"
                                                :key="'segment-' + row.id + '-' + segment.id + '-' + segment.display_start_date + '-' + segment.display_end_date"
                                                class="timeline-segment"
                                                :class="{
                                                    'timeline-segment-menu-open': activeSegmentMenuKey === segmentMenuKey(row, segment)
                                                }"
                                                :style="segmentStyle(segment, timelineSegmentIndex(row, segment))"
                                            >
                                                <div
                                                    v-if="!isCustomerTimelineRoute && segmentAssigneeUsers(segment).length > 0"
                                                    class="timeline-segment-assignees"
                                                >
                                                    <img
                                                        v-for="(user, index) in segmentAssigneeUsers(segment)"
                                                        :key="'segment-user-' + row.id + '-' + segment.id + '-' + user.id + '-' + index"
                                                        :src="getUserAvatarUrl(user)"
                                                        :alt="user.name || 'Avatar'"
                                                        :title="user.name || ''"
                                                        :class="index === 0 ? '' : '-ml-2'"
                                                        class="timeline-segment-assignee-avatar"
                                                    >
                                                </div>
                                                <app-tooltip
                                                    v-if="activeSegmentMenuKey !== segmentMenuKey(row, segment)"
                                                    class="timeline-segment-tooltip"
                                                    placement="bottom"
                                                    width-class="w-auto max-w-none"
                                                >
                                                    <span v-if="isBoundaryIconSegment(segment)" class="timeline-segment-boundary-icon-wrap">
                                                        <svg-vue
                                                            class="timeline-segment-boundary-icon"
                                                            :icon="boundarySegmentIcon(segment)"
                                                        ></svg-vue>
                                                    </span>
                                                    <span v-else class="timeline-segment-label">{{ segment.name }}</span>
                                                    <template #content>
                                                        <span class="timeline-segment-tooltip-content">
                                                            <span class="timeline-segment-tooltip-title">{{ segment.name }}</span>
                                                            <span v-if="segmentTooltipDescription(segment)" class="timeline-segment-tooltip-text">{{ segmentTooltipDescription(segment) }}</span>
                                                            <span class="timeline-segment-tooltip-text">{{ segmentDateRangeText(segment) }}</span>
                                                        </span>
                                                    </template>
                                                </app-tooltip>
                                                <span v-else-if="isBoundaryIconSegment(segment)" class="timeline-segment-boundary-icon-wrap">
                                                    <svg-vue
                                                        class="timeline-segment-boundary-icon"
                                                        :icon="boundarySegmentIcon(segment)"
                                                    ></svg-vue>
                                                </span>
                                                <span v-else class="timeline-segment-label timeline-segment-label-static">{{ segment.name }}</span>
                                                <div
                                                    v-if="segment.is_overdue || segment.is_completed"
                                                    class="timeline-segment-statuses"
                                                >
                                                    <app-tooltip
                                                        v-if="segment.is_overdue"
                                                        class="timeline-segment-status-tooltip"
                                                        placement="bottom"
                                                        width-class="w-auto max-w-none"
                                                    >
                                                        <span
                                                            class="timeline-segment-status-badge timeline-segment-status-badge-danger"
                                                        >
                                                            <svg class="h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                                <path d="M12 5a7 7 0 0 1 7 7v3.5l1.8 2.1a1 1 0 0 1-.76 1.65H4.96a1 1 0 0 1-.76-1.65L6 15.5V12a7 7 0 0 1 6-6.92V5zm0 16a2.5 2.5 0 0 0 2.45-2h-4.9A2.5 2.5 0 0 0 12 21z" fill="none" stroke="currentColor" stroke-width="1.8" stroke-linejoin="round"/>
                                                            </svg>
                                                        </span>
                                                        <template #content>
                                                            <span class="timeline-segment-status-tooltip-text">{{ $t('Overdue') }}</span>
                                                        </template>
                                                    </app-tooltip>
                                                    <app-tooltip
                                                        v-if="segment.is_completed"
                                                        class="timeline-segment-status-tooltip"
                                                        placement="bottom"
                                                        width-class="w-auto max-w-none"
                                                    >
                                                        <span
                                                            class="timeline-segment-status-badge timeline-segment-status-badge-success"
                                                        >
                                                            <svg class="h-4 w-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                                <path d="M20 6L9 17l-5-5" fill="none" stroke="currentColor" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                                                            </svg>
                                                        </span>
                                                        <template #content>
                                                            <span class="timeline-segment-status-tooltip-text">{{ $t('Completed') }}</span>
                                                        </template>
                                                    </app-tooltip>
                                                </div>
                                                <div v-if="showSegmentMenu(row)" v-on-clickaway="closeSegmentActionMenu" class="timeline-segment-actions">
                                                    <button
                                                        class="timeline-segment-menu-btn"
                                                        type="button"
                                                        @click.stop="toggleSegmentActionMenu(segmentMenuKey(row, segment))"
                                                    >
                                                        <svg class="w-3 h-3" viewBox="0 0 12 12" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                                                            <circle cx="6" cy="2" r="1.1" fill="currentColor"></circle>
                                                            <circle cx="6" cy="6" r="1.1" fill="currentColor"></circle>
                                                            <circle cx="6" cy="10" r="1.1" fill="currentColor"></circle>
                                                        </svg>
                                                    </button>
                                                    <div
                                                        v-show="activeSegmentMenuKey === segmentMenuKey(row, segment)"
                                                        class="timeline-segment-menu-panel"
                                                    >
                                                        <button
                                                            v-if="row.type === 'custom_entry' && row.can_edit"
                                                            class="timeline-segment-menu-link"
                                                            type="button"
                                                            @click="openCustomEntryEditorDialog(row)"
                                                        >
                                                            {{ $t('Edit') }}
                                                        </button>
                                                        <button
                                                            v-if="row.type === 'custom_entry' && canManageCustomEntryShares(row)"
                                                            class="timeline-segment-menu-link"
                                                            type="button"
                                                            @click="openCustomEntrySharingDialog(row)"
                                                        >
                                                            {{ $t('Share') }}
                                                        </button>
                                                        <button
                                                            v-if="row.type === 'custom_entry' && row.can_delete"
                                                            class="timeline-segment-menu-link text-left w-full"
                                                            type="button"
                                                            @click="openCustomEntryDeleteDialog(row)"
                                                        >
                                                            {{ $t('Delete') }}
                                                        </button>
                                                        <button
                                                            v-if="row.type !== 'custom_entry'"
                                                            class="timeline-segment-menu-link text-left w-full"
                                                            type="button"
                                                            @click="openRowManagePage(row)"
                                                        >
                                                            {{ $t('Edit position') }}
                                                        </button>
                                                        <button
                                                            v-if="row.type !== 'custom_entry' && !isCustomerTimelineRoute"
                                                            class="timeline-segment-menu-link text-left w-full"
                                                            type="button"
                                                            @click="openScheduleEditor(row)"
                                                        >
                                                            {{ $t('Edit schedules') }}
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </template>

                    <timeline-filter-sidebar
                        :open="filterSidebarOpen"
                        :search-filter="searchFilter"
                        :filter-selection="filterSelection"
                        :available-years="availableYears"
                        :position-filter-selection="positionFilterSelection"
                        :can-filter-companies="canFilterCompanies"
                        :company-filter-selection="companyFilterSelection"
                        :company-filter-dropdown-options="companyFilterDropdownOptions"
                        :sort-selection="sortSelection"
                        :per-page-selection="perPageSelection"
                        :has-active-filters="hasActiveFilters"
                        @update:searchFilter="searchFilter = $event"
                        @update:filterSelection="filterSelection = $event"
                        @update:positionFilterSelection="positionFilterSelection = $event"
                        @update:companyFilterSelection="companyFilterSelection = $event"
                        @update:sortSelection="sortSelection = $event"
                        @update:perPageSelection="perPageSelection = $event"
                        @filter-change="reloadTimelineForFilterChange"
                        @apply-filter-selection="applyFilterSelection"
                        @reset="resetTimelineFilters"
                    />

                    <div class="timeline-zoom-overlay">
                        <div class="timeline-zoom-footer-group">
                            <button
                                :disabled="!canZoomOut"
                                :class="!canZoomOut ? 'opacity-50 cursor-not-allowed' : ''"
                                class="timeline-zoom-footer-btn timeline-zoom-footer-btn-left"
                                type="button"
                                :aria-label="$t('Zoom out')"
                                :title="$t('Zoom out')"
                                @click="zoomOut"
                            >
                                <svg-vue class="h-3.5 w-3.5" icon="font-awesome.regular.minus"></svg-vue>
                            </button>
                            <button
                                :disabled="!canZoomIn"
                                :class="!canZoomIn ? 'opacity-50 cursor-not-allowed' : ''"
                                class="timeline-zoom-footer-btn timeline-zoom-footer-btn-right"
                                type="button"
                                :aria-label="$t('Zoom in')"
                                :title="$t('Zoom in')"
                                @click="zoomIn"
                            >
                                <svg-vue class="h-3.5 w-3.5" icon="font-awesome.regular.plus"></svg-vue>
                            </button>
                        </div>
                    </div>
                </div>

                <list-footer-pagination
                    :start="pageFrom"
                    :end="pageTo"
                    :total="totalPositions"
                    :current-page="currentPage"
                    :total-pages="totalPages"
                    nav-class="timeline-footer"
                    @prev="goToPreviousPage"
                    @next="goToNextPage"
                />
            </div>
        </div>

        <confirm-dialog
            :open="deleteTimelineEntryDialog.open"
            :title="$t('Delete custom timeline entry')"
            :text="$t('Are you sure you want to delete this custom timeline entry?')"
            :confirm-label="$t('Delete')"
            :close-label="$t('Cancel')"
            @close="closeCustomEntryDeleteDialog"
            @confirm="deleteCustomEntry"
        />

        <timeline-entry-editor-dialog
            :open="timelineEntryDialog.open"
            :customer-view="isCustomerTimelineRoute"
            :entry-id="timelineEntryDialog.entryId"
            @close="closeTimelineEntryDialog"
            @saved="handleTimelineEntrySaved"
        />

        <approval-management-dialog
            :open="timelineEntrySharingDialog.open"
            :internal-users="timelineEntrySharingDialog.internalUsers"
            :customer-users="timelineEntrySharingDialog.customerUsers"
            :guest-users="[]"
            :selected-internal-ids="timelineEntrySharingDialog.selectedInternalIds"
            :selected-customer-ids="timelineEntrySharingDialog.selectedCustomerIds"
            :selected-guest-ids="[]"
            :show-tab-bar="timelineEntrySharingShowTabs"
            :show-customer-tab="timelineEntrySharingShowCustomerTab"
            :show-guest-tab="false"
            :initial-tab="timelineEntrySharingInitialTab"
            :empty-internal-text="$t('No internal users available.')"
            :empty-customer-text="$t('No customer users available.')"
            @close="closeCustomEntrySharingDialog"
            @save="saveCustomEntrySharing"
        />
    </div>
</template>

<script>
import {mixin as clickaway} from "vue-clickaway";
import ConfirmDialog from "@/components/modals/confirm-dialog";
import ApprovalManagementDialog from "@/components/modals/approval-management-dialog";
import TimelineEntryEditorDialog from "@/components/timeline/timeline-entry-editor-dialog";
import TimelineFilterSidebar from "@/components/timeline/timeline-filter-sidebar";
import {
    buildSegmentMenuKey,
    buildSegmentStyle,
    buildTimelineMonths,
    buildTimelineWeeks,
    calculateDayOffset,
    calculateRowHeight,
    calculateTodayLeft,
    getChronologyStatusText,
    getTimelineSegmentIndex,
    getTimelineSegments,
    hasTimelineSegments,
    isRowMenuActive,
    resolveUserAvatarUrl,
} from "./position-timeline.helpers";
import {
    buildTimelineQueryParams,
    calculateDateAnchorScrollLeft,
    captureDateAnchor,
    mapTimelineResponse,
} from "./position-timeline.service";
import {resolveAccessState} from "@/utils/access-state";

export default {
    name: "position-timeline-base",
    mixins: [clickaway],
    components: {
        ConfirmDialog,
        ApprovalManagementDialog,
        TimelineEntryEditorDialog,
        TimelineFilterSidebar,
    },
    props: {
        customerView: {
            type: Boolean,
            default: false,
        },
    },
    metaInfo() {
        return {
            title: this.$t('Timeline')
        }
    },
    data() {
        return {
            loading: false,
            mode: 'current',
            selectedYear: null,
            availableYears: [],
            searchFilter: '',
            rangeStart: null,
            rangeEnd: null,
            today: null,
            rows: [],
            viewportWidth: window.innerWidth || 1280,
            zoomLevel: 1,
            filterSelection: 'current',
            positionFilterSelection: 'with_dates',
            canFilterCompanies: false,
            companyOptions: [],
            companyFilterSelection: 'all',
            exporting: false,
            filterSidebarOpen: false,
            activeSegmentMenuKey: null,
            sortColumn: 'title',
            sortOrder: 'asc',
            perPageSelection: 50,
            totalPositions: 0,
            currentPage: 1,
            totalPages: 1,
            pageFrom: 0,
            pageTo: 0,
            centerTodayTimeouts: [],
            pendingScrollRestore: null,
            pendingScrollRestoreTimeouts: [],
            isApplyingProgrammaticScroll: false,
            deleteTimelineEntryDialog: {
                open: false,
                row: null,
            },
            timelineEntryDialog: {
                open: false,
                entryId: null,
            },
            timelineEntrySharingDialog: {
                open: false,
                entry: null,
                internalUsers: [],
                customerUsers: [],
                selectedInternalIds: [],
                selectedCustomerIds: [],
            },
        };
    },
    computed: {
        displayRangeEnd() {
            if (!this.rangeEnd) {
                return null;
            }

            return moment(this.rangeEnd, 'YYYY-MM-DD')
                .endOf('isoWeek')
                .startOf('day')
                .format('YYYY-MM-DD');
        },
        totalDays() {
            if (!this.rangeStart || !this.rangeEnd) {
                return 0;
            }
            const start = moment(this.rangeStart, 'YYYY-MM-DD').startOf('day');
            const end = moment(this.displayRangeEnd, 'YYYY-MM-DD').startOf('day');
            return Math.max(0, end.diff(start, 'days') + 1);
        },
        timelineWidth() {
            const lastWeek = this.weeks.length > 0 ? this.weeks[this.weeks.length - 1] : null;
            const lastMonth = this.months.length > 0 ? this.months[this.months.length - 1] : null;
            const weekWidth = lastWeek ? (lastWeek.left + lastWeek.width) : 0;
            const monthWidth = lastMonth ? (lastMonth.left + lastMonth.width) : 0;

            return Math.max(1, weekWidth, monthWidth, this.totalDays * this.pixelsPerDay);
        },
        monthWidth() {
            return Math.max(160, (this.viewportWidth * 0.25) * this.zoomLevel);
        },
        pixelsPerDay() {
            return Math.max(2, this.monthWidth / 30.4375);
        },
        canZoomIn() {
            return this.zoomLevel < 3;
        },
        canZoomOut() {
            return this.zoomLevel > 0.4;
        },
        isCustomerTimelineRoute() {
            return this.customerView;
        },
        accessContext() {
            return resolveAccessState(this.$store);
        },
        canOpenCustomerPositionDetails() {
            return !!(this.accessContext && this.accessContext.modules && this.accessContext.modules.positions);
        },
        months() {
            return buildTimelineMonths(
                moment,
                this.rangeStart,
                this.displayRangeEnd,
                this.pixelsPerDay,
                (date) => this.dayOffset(date),
                window.app.app_date_locale
            );
        },
        weeks() {
            return buildTimelineWeeks(
                moment,
                this.rangeStart,
                this.displayRangeEnd,
                this.pixelsPerDay,
                (date) => this.dayOffset(date)
            );
        },
        todayLeft() {
            return calculateTodayLeft(
                this.today,
                this.rangeStart,
                this.displayRangeEnd,
                this.totalDays,
                this.pixelsPerDay,
                (date) => this.dayOffset(date)
            );
        },
        hasActiveFilters() {
            return this.mode !== 'current'
                || this.selectedYear !== null
                || (this.searchFilter || '').trim() !== ''
                || this.filterSelection !== 'current'
                || this.positionFilterSelection !== 'with_dates'
                || this.companyFilterSelection !== 'all'
                || this.sortColumn !== 'title'
                || this.sortOrder !== 'asc'
                || Number(this.perPageSelection || 0) !== 50;
        },
        sortSelection: {
            get() {
                return this.sortColumn + '_' + this.sortOrder;
            },
            set(value) {
                const normalizedValue = String(value || '');
                const lastUnderscoreIndex = normalizedValue.lastIndexOf('_');
                const column = lastUnderscoreIndex >= 0
                    ? normalizedValue.slice(0, lastUnderscoreIndex)
                    : normalizedValue;
                const order = lastUnderscoreIndex >= 0
                    ? normalizedValue.slice(lastUnderscoreIndex + 1)
                    : '';
                const sortColumn = ['title', 'updated_at', 'created_at'].includes(column)
                    ? column
                    : 'title';
                const sortOrder = ['asc', 'desc'].includes(order)
                    ? order
                    : 'asc';

                this.sortColumn = sortColumn;
                this.sortOrder = sortOrder;
            },
        },
        companyFilterDropdownOptions() {
            const companyOptions = (this.companyOptions || []).map((company) => ({
                ...company,
                id: String(company.id),
                name: company.name || '-',
                avatarUrl: this.getUserAvatarUrl(company),
            }));

            return [{id: 'all', name: this.$t('All companies').toString(), avatarUrl: ''}, ...companyOptions];
        },
        timelineEntrySharingShowCustomerTab() {
            return this.timelineEntrySharingDialog.customerUsers.length > 0;
        },
        timelineEntrySharingShowInternalTab() {
            return !this.isCustomerTimelineRoute && this.timelineEntrySharingDialog.internalUsers.length > 0;
        },
        timelineEntrySharingShowTabs() {
            return (this.timelineEntrySharingShowInternalTab ? 1 : 0) + (this.timelineEntrySharingShowCustomerTab ? 1 : 0) > 1;
        },
        timelineEntrySharingInitialTab() {
            return this.timelineEntrySharingShowInternalTab ? 'internal' : 'customer';
        },
    },
    mounted() {
        this.updateViewportWidth();
        window.addEventListener('resize', this.updateViewportWidth);
        window.addEventListener('load', this.handleWindowLoadCenter);
        this.fetchTimeline({
            centerToday: true,
            preserveScroll: false,
        });
    },
    activated() {
        this.centerTodayAfterNavigation();
    },
    deactivated() {
        this.clearCenterTodayTimeouts();
        this.clearPendingScrollRestoreTimeouts();
    },
    beforeUnmount() {
        window.removeEventListener('resize', this.updateViewportWidth);
        window.removeEventListener('load', this.handleWindowLoadCenter);
        this.clearCenterTodayTimeouts();
        this.clearPendingScrollRestoreTimeouts();
    },
    beforeRouteEnter(to, from, next) {
        next((vm) => {
            vm.centerTodayAfterNavigation();
        });
    },
    beforeRouteUpdate(to, from, next) {
        next();
        this.centerTodayAfterNavigation();
    },
    methods: {
        updateViewportWidth() {
            this.viewportWidth = window.innerWidth || 1280;
        },
        zoomIn() {
            this.zoomLevel = Math.min(3, Number((this.zoomLevel * 1.25).toFixed(2)));
        },
        zoomOut() {
            this.zoomLevel = Math.max(0.4, Number((this.zoomLevel / 1.25).toFixed(2)));
        },
        goToPreviousPage() {
            if (this.loading || this.currentPage <= 1) {
                return;
            }
            this.currentPage -= 1;
            this.fetchTimeline({
                centerToday: false,
                preserveScroll: false,
                preservePage: true,
            });
        },
        goToNextPage() {
            if (this.loading || this.currentPage >= this.totalPages) {
                return;
            }
            this.currentPage += 1;
            this.fetchTimeline({
                centerToday: false,
                preserveScroll: false,
                preservePage: true,
            });
        },
        onRefreshTimeline() {
            this.fetchTimeline({
                centerToday: false,
                preserveScroll: true,
                preserveDateAnchor: true,
                preservePage: true,
            });
        },
        toggleFilterSidebar() {
            this.filterSidebarOpen = !this.filterSidebarOpen;
        },
        reloadTimelineForFilterChange() {
            this.fetchTimeline({
                centerToday: true,
                preserveScroll: false,
                preserveDateAnchor: false,
            });
        },
        resetTimelineFilters() {
            this.mode = 'current';
            this.selectedYear = null;
            this.searchFilter = '';
            this.filterSelection = 'current';
            this.positionFilterSelection = 'with_dates';
            this.companyFilterSelection = 'all';
            this.sortColumn = 'title';
            this.sortOrder = 'asc';
            this.perPageSelection = 50;
            this.currentPage = 1;
            this.reloadTimelineForFilterChange();
        },
        centerTodayInViewOnce() {
            const shell = this.$refs.timelineShell;
            if (!shell || this.todayLeft === null) {
                return;
            }
            const maxLeft = this.maxTimelineScrollLeft(shell);
            const targetLeft = Math.max(0, Math.min(maxLeft, this.todayLeft - (shell.clientWidth / 2)));
            if (Math.abs(shell.scrollLeft - targetLeft) > 1) {
                shell.scrollLeft = targetLeft;
            }
        },
        scheduleCenterTodayInView() {
            this.clearCenterTodayTimeouts();
            const retryOffsets = [0, 120, 320, 650];
            retryOffsets.forEach((delay) => {
                const timeoutId = setTimeout(() => {
                    this.$nextTick(() => {
                        this.centerTodayInViewOnce();
                    });
                }, delay);
                this.centerTodayTimeouts.push(timeoutId);
            });
        },
        handleWindowLoadCenter() {
            this.scheduleCenterTodayInView();
        },
        clearCenterTodayTimeouts() {
            this.centerTodayTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
            this.centerTodayTimeouts = [];
        },
        clearPendingScrollRestoreTimeouts() {
            this.pendingScrollRestoreTimeouts.forEach((timeoutId) => clearTimeout(timeoutId));
            this.pendingScrollRestoreTimeouts = [];
        },
        maxTimelineContentRight() {
            const lastWeek = this.weeks.length > 0 ? this.weeks[this.weeks.length - 1] : null;
            const lastMonth = this.months.length > 0 ? this.months[this.months.length - 1] : null;
            const weekRight = lastWeek ? (lastWeek.left + lastWeek.width) : 0;
            const monthRight = lastMonth ? (lastMonth.left + lastMonth.width) : 0;

            return Math.max(weekRight, monthRight, 0);
        },
        maxTimelineScrollLeft(shell) {
            if (!shell) {
                return 0;
            }

            const desiredMax = Math.max(0, this.maxTimelineContentRight() - shell.clientWidth);
            const nativeMax = Math.max(0, shell.scrollWidth - shell.clientWidth);

            return Math.min(nativeMax, desiredMax);
        },
        clampTimelineScrollPosition(shell) {
            if (!shell) {
                return;
            }

            const maxLeft = this.maxTimelineScrollLeft(shell);
            if (shell.scrollLeft <= maxLeft) {
                return;
            }

            shell.scrollLeft = maxLeft;
        },
        onTimelineShellWheel(event) {
            const shell = this.$refs.timelineShell;
            if (!shell || !event) {
                return;
            }

            const horizontalDelta = Math.abs(event.deltaX) > Math.abs(event.deltaY)
                ? event.deltaX
                : 0;

            if (horizontalDelta <= 0) {
                return;
            }

            const maxLeft = this.maxTimelineScrollLeft(shell);
            const nextLeft = shell.scrollLeft + horizontalDelta;

            if (nextLeft <= maxLeft) {
                return;
            }

            event.preventDefault();
            shell.scrollLeft = maxLeft;
        },
        onTimelineShellScroll() {
            const shell = this.$refs.timelineShell;
            this.clampTimelineScrollPosition(shell);

            if (this.isApplyingProgrammaticScroll) {
                return;
            }

             // During timeline reloads the browser can emit scroll events while the
             // content width is rebuilding. Do not treat those as manual user scrolls,
             // otherwise the pending restore gets discarded and the view falls back to 0.
            if (this.loading && this.pendingScrollRestore) {
                return;
            }

            // Stop delayed auto-adjustments once the user scrolls manually.
            this.clearPendingScrollRestoreTimeouts();
            this.pendingScrollRestore = null;
            this.clearCenterTodayTimeouts();
        },
        scheduleScrollRestore() {
            this.clearPendingScrollRestoreTimeouts();
            const retryOffsets = [0, 80, 180, 360];
            retryOffsets.forEach((delay, index) => {
                const timeoutId = setTimeout(() => {
                    this.$nextTick(() => {
                        this.restoreTimelineScrollPosition(index === (retryOffsets.length - 1));
                    });
                }, delay);
                this.pendingScrollRestoreTimeouts.push(timeoutId);
            });
        },
        centerTodayAfterNavigation() {
            this.$nextTick(() => {
                this.scheduleCenterTodayInView();
            });
        },
        segmentMenuKey(row, segment) {
            return buildSegmentMenuKey(row, segment);
        },
        toggleSegmentActionMenu(key) {
            this.activeSegmentMenuKey = this.activeSegmentMenuKey === key ? null : key;
        },
        closeSegmentActionMenu() {
            this.activeSegmentMenuKey = null;
        },
        timelineSegments(row) {
            return getTimelineSegments(row);
        },
        timelineSegmentIndex(row, segment) {
            return getTimelineSegmentIndex(row, segment, (rowItem, segmentItem) => this.segmentMenuKey(rowItem, segmentItem));
        },
        rowHasActiveMenu(row) {
            return isRowMenuActive(row, this.activeSegmentMenuKey, (rowItem, segmentItem) => this.segmentMenuKey(rowItem, segmentItem));
        },
        chronologyStatusText(row) {
            return getChronologyStatusText(row, (key, replacements = {}) => this.$t(key, replacements).toString());
        },
        rowAvatar(row) {
            return row && row.avatar ? row.avatar : (row && row.company ? row.company : null);
        },
        showSegmentMenu(row) {
            if (!row) {
                return false;
            }

            if (row.type === 'custom_entry') {
                return !!(row.can_edit || row.can_delete || this.canManageCustomEntryShares(row));
            }

            return !this.isCustomerTimelineRoute;
        },
        segmentTooltipDescription(segment) {
            return String(segment?.short_description || '').trim();
        },
        openScheduleEditor(row) {
            if (this.isCustomerTimelineRoute) {
                return;
            }
            if (row && row.type === 'custom_entry') {
                this.openCustomEntryEditorDialog(row);
                return;
            }
            this.closeSegmentActionMenu();
            if (!row || !row.id) {
                return;
            }
            window.open('/workspace/positions/' + row.id + '/settings', '_blank');
        },
        rowManageRoute(row) {
            if (!row || !row.id) {
                return null;
            }

            if (row.type === 'custom_entry') {
                return null;
            }

            if (this.isCustomerTimelineRoute) {
                if (!this.canOpenCustomerPositionDetails) {
                    return null;
                }

                return '/positions/' + row.id;
            }

            return '/workspace/positions/' + row.id + '/manage';
        },
        openRowManagePage(row) {
            this.closeSegmentActionMenu();
            if (row && row.type === 'custom_entry') {
                this.openCustomEntryEditorDialog(row);
                return;
            }
            const route = this.rowManageRoute(row);
            if (!route) {
                return;
            }

            this.$router.push(route);
        },
        openNewTimelineEntryDialog() {
            this.closeSegmentActionMenu();
            this.timelineEntryDialog = {
                open: true,
                entryId: null,
            };
        },
        openCustomEntryEditorDialog(row) {
            this.closeSegmentActionMenu();
            this.timelineEntryDialog = {
                open: true,
                entryId: row && row.id ? Number(row.id) : null,
            };
        },
        closeTimelineEntryDialog() {
            this.timelineEntryDialog = {
                open: false,
                entryId: null,
            };
        },
        handleTimelineEntrySaved() {
            this.closeTimelineEntryDialog();
            this.fetchTimeline({
                centerToday: false,
                preserveScroll: true,
                preserveDateAnchor: true,
                preservePage: true,
            });
        },
        canManageCustomEntryShares(row) {
            return !!(row && row.type === 'custom_entry' && (row.can_manage_internal_shares || row.can_manage_customer_shares));
        },
        openCustomEntrySharingDialog(row) {
            this.closeSegmentActionMenu();
            if (!row || !row.id) {
                return;
            }

            axios.get(this.timelineApiEndpoint() + '/' + row.id).then((response) => {
                const payload = response.data || {};
                const entry = payload.entry || {};
                this.timelineEntrySharingDialog = {
                    open: true,
                    entry: entry,
                    internalUsers: Array.isArray(payload.internal_users) ? payload.internal_users : [],
                    customerUsers: Array.isArray(payload.customer_users) ? payload.customer_users : [],
                    selectedInternalIds: Array.isArray(entry.internal_user_ids) ? entry.internal_user_ids : [],
                    selectedCustomerIds: Array.isArray(entry.customer_user_ids) ? entry.customer_user_ids : [],
                };
            });
        },
        closeCustomEntrySharingDialog() {
            this.timelineEntrySharingDialog = {
                open: false,
                entry: null,
                internalUsers: [],
                customerUsers: [],
                selectedInternalIds: [],
                selectedCustomerIds: [],
            };
        },
        saveCustomEntrySharing(selection) {
            const entry = this.timelineEntrySharingDialog.entry;
            if (!entry || !entry.id) {
                this.closeCustomEntrySharingDialog();
                return;
            }

            const payload = {
                title: entry.title,
                short_description: entry.short_description,
                color: entry.color,
                starts_at: entry.starts_at,
                ends_at: entry.ends_at,
                internal_user_ids: Array.isArray(selection?.internal_ids) ? selection.internal_ids : [],
                customer_user_ids: Array.isArray(selection?.customer_ids) ? selection.customer_ids : [],
            };

            axios.put(this.timelineApiEndpoint() + '/' + entry.id, payload).then(() => {
                this.closeCustomEntrySharingDialog();
                this.fetchTimeline({
                    centerToday: false,
                    preserveScroll: true,
                    preserveDateAnchor: true,
                    preservePage: true,
                });
            });
        },
        applyFilterSelection() {
            if (this.filterSelection === 'current') {
                this.mode = 'current';
                this.selectedYear = null;
                this.reloadTimelineForFilterChange();
                return;
            }

            const parts = String(this.filterSelection).split(':');
            if (parts[0] === 'year' && parts[1]) {
                this.mode = 'year';
                this.selectedYear = Number(parts[1]);
                this.reloadTimelineForFilterChange();
            }
        },
        syncFilterSelection() {
            if (this.mode === 'year' && this.selectedYear) {
                this.filterSelection = 'year:' + this.selectedYear;
            } else {
                this.filterSelection = 'current';
            }
        },
        buildTimelineQueryParams(includePagination = true) {
            return buildTimelineQueryParams(this, includePagination);
        },
        applyTimelineResponse(data) {
            const mapped = mapTimelineResponse(data, this);
            this.availableYears = mapped.availableYears;
            this.searchFilter = mapped.searchFilter;
            this.rangeStart = mapped.rangeStart;
            this.rangeEnd = mapped.rangeEnd;
            this.today = mapped.today;
            this.rows = mapped.rows;
            this.totalPositions = mapped.totalPositions;
            this.canFilterCompanies = mapped.canFilterCompanies;
            this.companyOptions = mapped.companyOptions;
            this.sortColumn = mapped.sortColumn;
            this.sortOrder = mapped.sortOrder;
            this.perPageSelection = mapped.perPageSelection;
            this.currentPage = mapped.currentPage;
            this.totalPages = mapped.totalPages;
            this.pageFrom = mapped.pageFrom;
            this.pageTo = mapped.pageTo;
            this.positionFilterSelection = mapped.positionFilterSelection;
            this.companyFilterSelection = mapped.companyFilterSelection;
            this.selectedYear = mapped.selectedYear;

            this.syncFilterSelection();
        },
        fetchTimeline(options = {}) {
            const self = this;
            const config = {
                centerToday: false,
                preserveScroll: true,
                preserveDateAnchor: false,
                preservePage: false,
                ...options,
            };
            if (!config.preservePage) {
                self.currentPage = 1;
            }
            const shell = self.$refs.timelineShell;
            const prevScrollLeft = config.preserveScroll && shell ? shell.scrollLeft : null;
            const prevScrollTop = config.preserveScroll && shell ? shell.scrollTop : null;
            const dateAnchor = config.preserveDateAnchor
                ? captureDateAnchor(moment, self.rangeStart, self.pixelsPerDay, shell)
                : null;
            self.pendingScrollRestore = {
                config: config,
                prevScrollLeft: prevScrollLeft,
                prevScrollTop: prevScrollTop,
                dateAnchor: dateAnchor,
            };

            self.loading = true;
            const params = self.buildTimelineQueryParams(true);

            axios.get(self.timelineApiEndpoint(), {params: params}).then(function (response) {
                self.applyTimelineResponse(response.data || {});
            }).finally(function () {
                self.loading = false;
                self.$nextTick(() => {
                    self.scheduleScrollRestore();
                });
            });
        },
        restoreTimelineScrollPosition(isFinalPass = false) {
            if (!this.pendingScrollRestore) {
                return;
            }

            const restore = this.pendingScrollRestore;
            const nextShell = this.$refs.timelineShell;

            if (!nextShell) {
                if (isFinalPass) {
                    this.pendingScrollRestore = null;
                }
                return;
            }

            if (restore.config.centerToday) {
                this.scheduleCenterTodayInView();
                if (isFinalPass) {
                    this.pendingScrollRestore = null;
                }
                return;
            }

            if (restore.dateAnchor && this.rangeStart) {
                const targetLeft = calculateDateAnchorScrollLeft(
                    moment,
                    this.rangeStart,
                    this.pixelsPerDay,
                    restore.dateAnchor,
                    nextShell
                );
                if (targetLeft !== null) {
                    this.isApplyingProgrammaticScroll = true;
                    nextShell.scrollLeft = Math.min(targetLeft, this.maxTimelineScrollLeft(nextShell));
                }
                if (restore.prevScrollTop !== null) {
                    this.isApplyingProgrammaticScroll = true;
                    nextShell.scrollTop = restore.prevScrollTop;
                }
                setTimeout(() => {
                    this.isApplyingProgrammaticScroll = false;
                }, 0);
                if (isFinalPass) {
                    this.pendingScrollRestore = null;
                }
                return;
            }

            if (restore.config.preserveScroll && restore.prevScrollLeft !== null && restore.prevScrollTop !== null) {
                this.isApplyingProgrammaticScroll = true;
                nextShell.scrollLeft = Math.min(restore.prevScrollLeft, this.maxTimelineScrollLeft(nextShell));
                nextShell.scrollTop = restore.prevScrollTop;
                setTimeout(() => {
                    this.isApplyingProgrammaticScroll = false;
                }, 0);
            }
            if (isFinalPass) {
                this.pendingScrollRestore = null;
            }
        },
        centerTodayInView() {
            this.centerTodayInViewOnce();
        },
        exportTimeline() {
            const self = this;
            self.exporting = true;
            const params = self.buildTimelineQueryParams(false);

            axios.get(self.timelineExportApiEndpoint(), {
                params: params,
                responseType: 'blob',
            }).then(function (response) {
                const blob = new Blob([response.data], {type: 'text/csv;charset=utf-8;'});
                const link = document.createElement('a');
                const contentDisposition = response.headers && response.headers['content-disposition']
                    ? String(response.headers['content-disposition'])
                    : '';
                const match = contentDisposition.match(/filename="?([^"]+)"?/);
                const filename = match && match[1] ? match[1] : 'timeline_export.csv';
                link.href = window.URL.createObjectURL(blob);
                link.setAttribute('download', filename);
                document.body.appendChild(link);
                link.click();
                document.body.removeChild(link);
                window.URL.revokeObjectURL(link.href);
                self.exporting = false;
            }).catch(function () {
                self.exporting = false;
            });
        },
        dayOffset(dateString) {
            return calculateDayOffset(moment, this.rangeStart, dateString);
        },
        rowHeight(row) {
            return calculateRowHeight(row);
        },
        hasSegments(row) {
            return hasTimelineSegments(row);
        },
        segmentAssigneeUsers(segment) {
            if (!segment || !Array.isArray(segment.reader_users)) {
                return [];
            }

            return segment.reader_users.filter((user) => user && this.getUserAvatarUrl(user));
        },
        isBoundaryIconSegment(segment) {
            return !!(segment && (segment.is_mandatory_start || segment.is_mandatory_completion));
        },
        boundarySegmentIcon(segment) {
            if (segment && segment.is_mandatory_completion) {
                return 'font-awesome.regular.arrow-right-to-line';
            }

            return 'font-awesome.regular.arrow-left-to-line';
        },
        segmentStyle(segment, index) {
            return buildSegmentStyle(segment, index, this.pixelsPerDay, (date) => this.dayOffset(date), {
                customerView: this.isCustomerTimelineRoute,
            });
        },
        segmentDateRangeText(segment) {
            if (!segment || !segment.start_date || !segment.end_date) {
                return this.$t('Schedule open').toString();
            }

            const start = moment(segment.start_date, 'YYYY-MM-DD');
            const end = moment(segment.end_date, 'YYYY-MM-DD');

            if (!start.isValid() || !end.isValid()) {
                return segment.start_date + ' - ' + segment.end_date;
            }

            return start.format('DD.MM.YYYY') + ' - ' + end.format('DD.MM.YYYY');
        },
        weekRangeText(week) {
            if (!week || !week.start_date || !week.end_date) {
                return '';
            }

            const start = moment(week.start_date, 'YYYY-MM-DD');
            const end = moment(week.end_date, 'YYYY-MM-DD');

            return start.format('DD.MM.YYYY') + ' - ' + end.format('DD.MM.YYYY');
        },
        rowProjects(row) {
            const projects = Array.isArray(row?.projects)
                ? row.projects
                : (row?.projects && Array.isArray(row.projects.data) ? row.projects.data : []);

            if (projects.length > 0) {
                return projects.filter((project) => project && Number(project.id) > 0);
            }

            if (row && row.project && Number(row.project.id) > 0) {
                return [row.project];
            }

            return [];
        },
        rowProjectImageUrl(project) {
            if (project && project.image_url) {
                return project.image_url;
            }

            return '/images/default/icon.png';
        },
        rowProjectTooltip(project) {
            const title = String(project?.title || '').trim();
            return title !== ''
                ? this.$t('Project: :title', {title}).toString()
                : this.$t('Project').toString();
        },
        rowDisplayTitle(row) {
            if (row && row.type === 'custom_entry') {
                return this.$t('Custom timeline entry').toString();
            }

            return String(row?.title || '');
        },
        getUserAvatarUrl(user) {
            return resolveUserAvatarUrl(user);
        },
        openCustomEntryDeleteDialog(row) {
            this.closeSegmentActionMenu();
            this.deleteTimelineEntryDialog = {
                open: true,
                row: row || null,
            };
        },
        closeCustomEntryDeleteDialog() {
            this.deleteTimelineEntryDialog = {
                open: false,
                row: null,
            };
        },
        deleteCustomEntry() {
            const row = this.deleteTimelineEntryDialog.row;
            if (!row || row.type !== 'custom_entry' || !row.id) {
                this.closeCustomEntryDeleteDialog();
                return;
            }

            axios.delete(this.timelineApiEndpoint() + '/' + row.id).then(() => {
                this.closeCustomEntryDeleteDialog();
                this.reloadTimelineForFilterChange();
            });
        },
        timelineApiEndpoint() {
            return this.isCustomerTimelineRoute
                ? 'api/timeline'
                : 'api/workspace/timeline';
        },
        timelineExportApiEndpoint() {
            return this.isCustomerTimelineRoute
                ? 'api/timeline/export'
                : 'api/workspace/timeline/export';
        },
    },
}
</script>

<style scoped>
.timeline-page {
    height: 100%;
    display: flex;
    flex-direction: column;
    min-height: 0;
    --timeline-header-bg: var(--ui-surface-raised);
    --timeline-border-color: var(--ui-border-subtle);
}

.timeline-shell {
    overflow-x: auto;
    overflow-y: auto;
    flex: 1;
    min-height: 0;
    height: 100%;
    max-height: 100%;
    background: #ffffff;
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    overscroll-behavior: contain;
}

.timeline-header {
    position: sticky;
    top: 0;
    z-index: 30;
    display: block;
    background: var(--timeline-header-bg);
}

.timeline-right-header {
    position: relative;
    min-height: 50px;
    background: var(--timeline-header-bg);
}

.timeline-month-row {
    position: relative;
    height: 26px;
    background: var(--timeline-header-bg);
}

.timeline-month-cell {
    position: absolute;
    top: 0;
    bottom: 0;
    padding: 5px 8px;
    font-size: 11px;
    font-weight: 600;
    color: var(--ui-text-secondary);
    display: flex;
    align-items: center;
    justify-content: center;
    text-align: center;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.timeline-week-row {
    position: relative;
    height: 24px;
    background: var(--timeline-header-bg);
}

.timeline-week-marker {
    position: absolute;
    top: 0;
    bottom: 0;
    display: flex;
    align-items: center;
    justify-content: center;
    transition: background-color 0.12s ease-in-out;
}

.timeline-week-marker:hover {
    background: var(--ui-border-subtle);
}

.timeline-week-tooltip {
    width: 100%;
    height: 100%;
    display: inline-flex;
    align-items: center;
    justify-content: center;
}

.timeline-week-tooltip-text {
    display: inline-block;
    font-size: 11px;
    line-height: 15px;
    color: var(--ui-text-subtle);
    font-weight: 500;
    white-space: nowrap;
}

.timeline-week-label {
    font-size: 11px;
    color: var(--ui-text-subtle);
    white-space: nowrap;
    line-height: 1.5;
    font-weight: 500;
    text-align: center;
}

.timeline-today-overlay {
    position: absolute;
    top: 0;
    bottom: 0;
    width: 0;
    border-left: 2px solid #dc2626;
    pointer-events: none;
    z-index: 20;
}

.timeline-body {
    position: relative;
    flex: 1;
    background: #ffffff;
    min-height: 0;
}

.timeline-grid-fill {
    position: absolute;
    top: 0;
    bottom: 0;
    border-left: 1px solid var(--timeline-border-color);
    pointer-events: none;
    z-index: 1;
}

.timeline-grid-fill-cell {
    position: absolute;
    top: 0;
    bottom: 0;
}

.timeline-rows-overlay {
    position: relative;
    z-index: 2;
    min-height: 100%;
    background: transparent;
}

.timeline-row {
    display: block;
}

.timeline-right-cell {
    position: relative;
    background: transparent;
    z-index: 2;
}

.timeline-right-cell-menu-open {
    z-index: 220;
}

.timeline-track-label-wrap {
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    top: 0;
    z-index: 4;
    display: block;
    padding-top: 12px;
    pointer-events: none;
}

.timeline-track-label-wrap-no-segments {
    height: auto;
    display: block;
    align-items: initial;
    padding-top: 12px;
}

.timeline-track-label {
    position: sticky;
    left: 14px;
    display: flex;
    align-items: center;
    width: fit-content;
    max-width: 520px;
    padding: 3px 10px;
    border-radius: 4px;
    background: rgba(255, 255, 255, 0.92);
    color: var(--ui-text-strong);
    font-size: 13px;
    font-weight: 600;
    text-decoration: none;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    pointer-events: auto;
}

.timeline-track-avatar {
    width: 21px;
    height: 21px;
    border-radius: 9999px;
    object-fit: cover;
    border: 1px solid #ffffff;
    margin-right: 6px;
    flex-shrink: 0;
}

.timeline-track-projects {
    display: flex;
    align-items: center;
    margin-right: 3px;
    flex-shrink: 0;
}

.timeline-track-project-avatar {
    width: 21px;
    height: 21px;
    border-radius: 4px;
    object-fit: cover;
    border: 1px solid #ffffff;
    flex-shrink: 0;
}

.timeline-track-title {
    font-size: 0.875rem;
    line-height: 1.4;
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
}

.timeline-track-meta {
    margin-left: 6px;
    font-size: 12px;
    font-weight: 500;
    color: var(--ui-text-subtle);
    white-space: nowrap;
    flex-shrink: 0;
}

.timeline-track-meta-label {
    margin-left: 8px;
    min-height: 24px;
    display: inline-flex;
    align-items: center;
    padding: 0px 5px;
    border: 1px solid var(--ui-border-default);
    border-radius: 4px;
    background: var(--ui-surface-raised);
    color: var(--ui-text-subtle);
    font-size: 11px;
    line-height: 1.5;
    font-weight: 500;
    white-space: nowrap;
    word-break: keep-all;
    overflow-wrap: normal;
    flex-shrink: 0;
}

.timeline-track-label:hover {
    color: var(--ui-text-heading);
}

.timeline-segment {
    position: absolute;
    height: 30px;
    display: flex;
    align-items: center;
    overflow: visible;
    z-index: 3;
    cursor: default;
}

.timeline-segment-assignees {
    position: absolute;
    right: calc(100% - 6px);
    top: 50%;
    left: auto;
    margin-right: 0;
    transform: translateY(-50%);
    display: flex;
    align-items: center;
    width: max-content;
    white-space: nowrap;
    pointer-events: auto;
    z-index: 36;
}

.timeline-segment-assignee-avatar {
    display: block;
    width: 24px;
    height: 24px;
    flex-shrink: 0;
    border-radius: 9999px;
    border: 1px solid #ffffff;
    object-fit: cover;
    background: #ffffff;
    box-shadow: 0 1px 2px rgba(17, 24, 39, 0.1);
}

.timeline-segment:hover,
.timeline-segment:focus-within {
    z-index: 140;
}

.timeline-segment-menu-open {
    z-index: 240;
}

.timeline-segment-label {
    display: block;
    flex: 1 1 auto;
    min-width: 0;
    box-sizing: border-box;
    width: 100%;
    height: 100%;
    padding: 0 11px;
    line-height: 30px;
    font-size: 12px;
    font-weight: 600;
    color: rgba(255, 255, 255, 0.95);
    white-space: nowrap;
    overflow: hidden;
    text-overflow: ellipsis;
    user-select: none;
}

.timeline-segment-label-static {
    display: block;
    flex: 1 1 auto;
    min-width: 0;
    height: 100%;
}

.timeline-segment-boundary-icon-wrap {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 100%;
    height: 100%;
    padding: 0 10px;
    color: rgba(255, 255, 255, 0.95);
}

.timeline-segment-boundary-icon {
    width: 14px;
    height: 14px;
    flex-shrink: 0;
}

.timeline-segment-tooltip {
    position: relative;
    display: flex;
    align-items: center;
    flex: 1 1 auto;
    min-width: 0;
    height: 100%;
    width: auto;
    max-width: none;
    overflow: hidden;
    z-index: 150;
}

:deep(.cp-tooltip-wrap.timeline-segment-tooltip) {
    display: block !important;
    width: 100% !important;
    min-width: 0 !important;
    max-width: 100% !important;
    height: 100%;
}

.timeline-segment-tooltip-content {
    display: inline-flex;
    flex-direction: column;
    gap: 1px;
    white-space: nowrap;
}

.timeline-segment-tooltip-title {
    font-size: 11px;
    line-height: 15px;
    color: var(--ui-text-subtle);
    font-weight: 500;
}

.timeline-segment-tooltip-text {
    font-size: 11px;
    line-height: 15px;
    color: var(--ui-text-subtle);
    font-weight: 500;
}

.timeline-segment-actions {
    position: absolute;
    right: 6px;
    top: 50%;
    transform: translateY(-50%);
    z-index: 250;
}

.timeline-segment-statuses {
    position: absolute;
    top: 50%;
    left: calc(100% + 18px);
    transform: translateY(-50%);
    display: inline-flex;
    align-items: center;
    gap: 6px;
    z-index: 35;
}

.timeline-segment-status-badge {
    position: relative;
    width: 36px;
    height: 30px;
    border-radius: 4px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: #ffffff;
    border: 2px solid;
    box-shadow: 0 2px 4px rgba(17, 24, 39, 0.08);
}

.timeline-segment-status-badge::before {
    content: '';
    position: absolute;
    left: -5px;
    top: 50%;
    width: 8px;
    height: 8px;
    background: #ffffff;
    border-left: 2px solid currentColor;
    border-bottom: 2px solid currentColor;
    transform: translateY(-50%) rotate(45deg);
}

.timeline-segment-status-badge-danger {
    color: #d15f5f;
    border-color: #d15f5f;
}

.timeline-segment-status-badge-success {
    color: #45ad61;
    border-color: #45ad61;
}

.timeline-segment-status-tooltip {
    position: relative;
    display: inline-flex;
    z-index: 150;
}

.timeline-segment-status-tooltip-text {
    display: inline-block;
    font-size: 11px;
    line-height: 15px;
    color: var(--ui-text-subtle);
    font-weight: 500;
    white-space: nowrap;
    word-break: keep-all;
    overflow-wrap: normal;
}

.timeline-segment-menu-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 14px;
    height: 14px;
    color: rgba(255, 255, 255, 0.9);
    background: transparent;
    border: 0;
    padding: 0;
    cursor: pointer;
}

.timeline-segment-menu-panel {
    position: absolute;
    right: 0;
    top: 100%;
    margin-top: 8px;
    min-width: 180px;
    border-radius: 4px;
    background: #fff;
    box-shadow: 0 10px 20px rgba(17, 24, 39, 0.12);
    border: 1px solid var(--ui-border-subtle);
    overflow: hidden;
    z-index: 260;
}

.timeline-segment-menu-link {
    display: block;
    width: 100%;
    border: 0;
    background: transparent;
    padding: 8px 14px;
    font-size: 14px;
    line-height: 20px;
    color: var(--ui-text-secondary);
    text-decoration: none;
    text-align: left;
}

.timeline-segment-menu-link:hover {
    background: var(--ui-surface-muted);
}

.timeline-zoom-footer-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    min-width: 44px;
    height: 44px;
    padding: 0;
    border: 1px solid var(--ui-border-default);
    background: #ffffff;
    color: var(--ui-text-default);
    font-size: 14px;
    line-height: 1;
    font-weight: 600;
    box-shadow: 0 1px 3px rgba(17, 24, 39, 0.08);
}

.timeline-zoom-footer-group {
    display: inline-flex;
    align-items: center;
}

.timeline-zoom-overlay {
    position: absolute;
    right: 24px;
    bottom: 24px;
    z-index: 45;
}

.timeline-zoom-footer-btn-left {
    border-top-left-radius: 4px;
    border-bottom-left-radius: 4px;
    border-top-right-radius: 0;
    border-bottom-right-radius: 0;
}

.timeline-zoom-footer-btn-right {
    border-left: 0;
    border-top-left-radius: 0;
    border-bottom-left-radius: 0;
    border-top-right-radius: 4px;
    border-bottom-right-radius: 4px;
}

.timeline-pagination-link:disabled {
    opacity: 1;
    cursor: not-allowed;
}

.timeline-pagination-link {
    border-radius: 4px;
}

.timeline-shell .border-bordergray-100 {
    border-color: var(--timeline-border-color);
}
</style>
